import { Phone, Mail, Clock, Instagram, Facebook, MapPin } from "lucide-react";

const mobileLinks = [
  ["/delivery", "Delivery"], ["/rates", "Rates"], ["/faq", "FAQ"], ["/partners", "Partners"],
  ["/fleet", "Fleet"], ["/luxury-car-rental-vancouver", "Luxury Rental"], ["/exotic-car-rental-vancouver", "Exotic Rental"],
  ["/wedding-car-rental-vancouver", "Wedding Rental"], ["/yvr-airport-car-rental", "YVR Airport"], ["/rent-out-your-car", "Consign"],
  ["/privacy", "Privacy Policy"], ["/terms", "Terms"],
  ["/about", "About"], ["/blog", "Guides"],
] as const;

function MobileFooter() {
  const h = "text-[11px] uppercase tracking-[0.15em] text-foreground mb-3";
  return (
    <div className="md:hidden px-6 py-8 space-y-6 text-left">
      <div>
        <Logo size="md" className="!items-start" />
        <p className="mt-3 text-sm text-muted-foreground">Luxury car rental, delivered across Metro Vancouver.</p>
      </div>
      <div>
        <h2 className={h}>Contact</h2>
        <ul className="space-y-3 text-sm text-muted-foreground">
          <li><a href="tel:+17783798881" className="flex items-center gap-3"><Phone size={14} className="shrink-0 text-foreground" />+1-778-379-8881</a></li>
          <li><a href="mailto:info@avoluscars.com" className="flex items-center gap-3"><Mail size={14} className="shrink-0 text-foreground" />info@avoluscars.com</a></li>
          <li className="flex items-start gap-3"><MapPin size={14} className="mt-0.5 shrink-0 text-foreground" /><span><span className="block whitespace-nowrap text-[12px] tracking-tight">250 - 997 Seymour St #1711, Vancouver, BC V6B 3M1</span><span className="block text-xs text-muted-foreground/80">Mailing address only, we deliver.</span></span></li>
        </ul>
      </div>
      <div>
        <h2 className={h}>Hours</h2>
        <p className="text-sm text-muted-foreground">Mon-Fri&nbsp; 8 AM - 8 PM</p>
        <p className="text-sm text-muted-foreground">Sat-Sun&nbsp; 8 AM - 8 PM</p>
        <p className="text-sm text-muted-foreground">After-hours delivery by appointment</p>
      </div>
      <ul className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm text-muted-foreground">
        {mobileLinks.map(([to, label]) => (<li key={to}><a href={to} className="hover:text-foreground">{label}</a></li>))}
      </ul>
      <div className="flex items-center gap-5 text-foreground">
        <a href="https://instagram.com/avoluscars" target="_blank" rel="noreferrer" aria-label="Instagram"><Instagram size={20} /></a>
        <a href="https://facebook.com/avoluscars" target="_blank" rel="noreferrer" aria-label="Facebook"><Facebook size={20} /></a>
      </div>
      <p className="text-xs text-muted-foreground">© {new Date().getFullYear()} Avolus Cars. All rights reserved.</p>
    </div>
  );
}
import { Logo } from "./Logo";

export function Footer() {
  return (
    <footer className="border-t border-border bg-background">
      <MobileFooter />
      <div className="hidden md:block mx-auto max-w-7xl px-6 lg:px-10 py-20">
        <div className="grid gap-12 md:grid-cols-5">
          <div className="md:col-span-1">
            <Logo size="md" className="mb-5" />
            <p className="text-sm text-muted-foreground leading-relaxed max-w-xs">
              West coast car rental done right. Curated luxury and exotic vehicles, delivered
              across Metro Vancouver.
            </p>
            <div className="mt-6 flex items-center gap-3">
              <a
                href="https://instagram.com/avoluscars"
                target="_blank"
                rel="noreferrer"
                aria-label="Instagram"
                className="h-10 w-10 inline-flex items-center justify-center border border-border text-foreground hover:bg-foreground hover:text-background transition-colors"
              >
                <Instagram size={16} />
              </a>
              <a
                href="https://facebook.com/avoluscars"
                target="_blank"
                rel="noreferrer"
                aria-label="Facebook"
                className="h-10 w-10 inline-flex items-center justify-center border border-border text-foreground hover:bg-foreground hover:text-background transition-colors"
              >
                <Facebook size={16} />
              </a>
            </div>
          </div>

          <div>
            <h2 className="text-xs uppercase tracking-[0.25em] text-foreground mb-5">
              Contact Us
            </h2>
            <ul className="space-y-3 text-sm text-muted-foreground">
              <li className="flex items-start gap-3">
                <Phone size={14} className="mt-1 text-foreground" />
                <a href="tel:+17783798881" className="hover:text-foreground transition-colors">
                  +1-778-379-8881
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Mail size={14} className="mt-1 text-foreground" />
                <a href="mailto:info@avoluscars.com" className="hover:text-foreground transition-colors">
                  info@avoluscars.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={14} className="mt-1 text-foreground" />
                <span>
                  250 - 997 Seymour St #1711<br />
                  Vancouver, BC V6B 3M1<br />
                  <span className="text-foreground/70">Mailing address only. Delivery service, no physical pickup location</span>
                </span>
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-xs uppercase tracking-[0.25em] text-foreground mb-5">
              Hours of Operation
            </h2>
            <ul className="space-y-3 text-sm text-muted-foreground">
              <li className="flex items-start gap-3">
                <Clock size={14} className="mt-1 text-foreground" />
                <div>
                  <div>Monday - Friday</div>
                  <div className="text-foreground/80">8:00 AM - 8:00 PM</div>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Clock size={14} className="mt-1 text-foreground" />
                <div>
                  <div>Saturday - Sunday</div>
                  <div className="text-foreground/80">8:00 AM - 8:00 PM</div>
                </div>
              </li>
              <li className="text-xs text-muted-foreground pt-2">
                Private after-hours delivery by appointment.
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-xs uppercase tracking-[0.25em] text-foreground mb-5">
              Explore
            </h2>
            <ul className="space-y-3 text-sm text-muted-foreground">
              <li><a href="/delivery" className="hover:text-foreground transition-colors">Delivery</a></li>
              <li><a href="/rates" className="hover:text-foreground transition-colors">Rates</a></li>
              <li><a href="/faq" className="hover:text-foreground transition-colors">FAQ</a></li>
              <li><a href="/about" className="hover:text-foreground transition-colors">About Avolus</a></li>
              <li><a href="/blog" className="hover:text-foreground transition-colors">Rental Guides</a></li>
              <li><a href="/luxury-car-rental-vancouver" className="hover:text-foreground transition-colors">Luxury Car Rental Vancouver</a></li>
              <li><a href="/exotic-car-rental-vancouver" className="hover:text-foreground transition-colors">Exotic Car Rental Vancouver</a></li>
              <li><a href="/wedding-car-rental-vancouver" className="hover:text-foreground transition-colors">Wedding Car Rental Vancouver</a></li>
              <li><a href="/yvr-airport-car-rental" className="hover:text-foreground transition-colors">YVR Airport Car Rental</a></li>
              <li><a href="/rent-out-your-car" className="hover:text-foreground transition-colors">Consign</a></li>
            </ul>
          </div>

          <div className="flex flex-col">
            <h2 className="text-xs uppercase tracking-[0.25em] text-foreground mb-5">
              Ready to drive?
            </h2>
            <p className="text-sm text-muted-foreground mb-5">
              Book your vehicle in under two minutes.
            </p>
            <a
              href="/book"
              className="inline-flex justify-center text-xs uppercase tracking-[0.25em] px-6 py-4 bg-foreground text-background hover:bg-foreground/90 hover:tracking-[0.32em] hover:scale-[1.02] transition-all duration-300"
            >
              Book Now
            </a>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-border flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} Avolus Cars. All rights reserved.
          </p>
          <p className="text-xs text-muted-foreground flex items-center gap-4">
            <a href="/privacy" className="hover:text-foreground transition-colors">Privacy Policy</a>
            <a href="/terms" className="hover:text-foreground transition-colors">Terms &amp; Conditions</a>

          </p>
          <p className="text-xs text-muted-foreground tracking-[0.2em] uppercase">
            Drive Extraordinary
          </p>
        </div>
      </div>
    </footer>
  );
}
